import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useVAdminSelectedIncident } from "#root/queries/local";
import { useUpdateFamilyAssistance, useFamilyAssistanceById } from "#root/queries/familyAssistance";
import { FILE_KINDS, FILE_REFERENCE_TYPES, useFileUpload } from "#root/hooks/useFileUpload";
import { useFileDelete } from "#root/hooks/useFileDelete";
import { urlToFile } from "#root/services/utils";
import { showToast } from "#root/components/GlobalToaster";
import { Breadcrumbs } from "#root/components/Breadcrumbs";
import { ROUTES } from "#root/routes/constants";
import FamilyAssistanceForm, { FamilyAssistanceFormData } from "../FamilyAssistanceForm";
import { UploaderFile } from "#root/components/ImageUploader";
import { Spinner } from "@fluentui/react-components";

const EditFamilyAssistance: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { selectedIncidentId, setVAdminSelectedIncidentId } = useVAdminSelectedIncident();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletedFileIds, setDeletedFileIds] = useState<string[]>([]);
  const [formData, setFormData] = useState<FamilyAssistanceFormData | undefined>();
  const [initialName, setInitialName] = useState('');

  const { deleteImages } = useFileDelete();
  const { uploadImages } = useFileUpload();

  const { data: familyAssistanceResponse, isLoading: isFamilyAssistanceLoading } = useFamilyAssistanceById({ id: id || "" });
  const updateFamilyAssistance = useUpdateFamilyAssistance();

  const familyAssistance = familyAssistanceResponse?.item;

  useEffect(() => {
    if (familyAssistance) {
      const updateForm = async () => {
        let file = familyAssistance.photoUrl
          ? await urlToFile(familyAssistance.photoUrl, familyAssistance.photoId).catch(() => null)
          : null;

        const formData: FamilyAssistanceFormData = {
          ...familyAssistance,
          counselorPreferences: familyAssistance.counselorPreferences || [],
          specifyDisabilities: familyAssistance.specifyDisabilities || [],
          needs: familyAssistance.needs || [],
          familyAssistanceImage: file,
          photoUrl: familyAssistance.photoUrl || "",
          photoId: familyAssistance.photoId || "",
        };
        setFormData(formData);
        setVAdminSelectedIncidentId(familyAssistance.incidentId);
      };
      updateForm();
    }
  }, [familyAssistance]);

  useEffect(() => {
    if (familyAssistance) {
      const { firstName, lastName } = familyAssistance;
      setInitialName(`${firstName} ${lastName}`);
    }
  }, [familyAssistance]);

  if (!id) {
    return <div>Error: no id provided</div>;
  }

  if (isFamilyAssistanceLoading || !formData) {
    return <Spinner size="large" />;
  }

  const onFileDelete = (file: UploaderFile) => {
    if (file?.id && typeof file.id === 'string') {
      setDeletedFileIds((prev) => [...prev, file.id as string]);
    }
  };

  const onFieldChange = (name: string, value: any) => {
    setFormData((prev) => prev ? { ...prev, [name]: value } : undefined);
  };

  const handleSubmit = async (formData: FamilyAssistanceFormData) => {
    setIsSubmitting(true);

    const payload = {
      ...formData,
      counselorPreferences: formData.counselorPreferences,
      ...(formData.hasDisabilities ? {} : { specifyDisabilities: undefined }),
      incidentId: selectedIncidentId as string,
    };

    try {
      if (deletedFileIds.length > 0) {
        await deleteImages(deletedFileIds);
      }

      if (formData.familyAssistanceImage instanceof File && formData.familyAssistanceImage.id == null) {
        const uploadPayload = [{
          file: formData.familyAssistanceImage,
          referenceId: id,
          kind: FILE_KINDS.FAMILY_ASSISTANCE,
          referenceType: FILE_REFERENCE_TYPES.FAMILY_ASSISTANCE,
        }];
        await uploadImages(uploadPayload);
      }

      await updateFamilyAssistance.mutateAsync({ id, familyAssistance: payload });
      showToast('Record updated successfully', 'success');
      navigate(ROUTES.VADMIN.FAMILY_ASSISTANCE_ENTRIES);
    } catch (error) {
      showToast('Error updating record', 'error');
      console.error(error);
    }
    finally{
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(ROUTES.VADMIN.FAMILY_ASSISTANCE_ENTRIES);
  };

  return (
    <div className="edit-family-assistance">
      <div className="emc-page-header">
        <div className="emc-page-header__top-row">
          <div className="emc-page-header__breadcrumbs">
            <Breadcrumbs currentPageName={initialName} />
          </div>
        </div>
        <div className="emc-page-header__title">
          <h2>Edit Family Assistance <div className="divider"></div></h2>
        </div>
      </div>

      <FamilyAssistanceForm
        formData={formData}
        onFieldChange={onFieldChange}
        onSubmit={handleSubmit}
        submitButtonText="Save"
        isSubmitting={isSubmitting}
        onFileDelete={onFileDelete}
        onCancel={handleCancel}
        isEdit={true}
      />
    </div>
  );
};

export default EditFamilyAssistance;
